import { isS12nEligibleForCourseraPlusUpgrade } from 'bundles/coursera-plus-common/utils/courseraPlusUpgradeUtils';
import {
  ENROLL_THROUGH_S12N_SUBSCRIPTION,
  PURCHASE_SINGLE_COURSE,
} from 'bundles/enroll-common/common/EnrollmentChoiceTypes';
import type { EnrollmentChoices } from 'bundles/enroll-common/components/xdp/withEnrollment';
import {
  choiceTypeToHandleSubmitPromise,
  submitEnrollmentPromise,
} from 'bundles/enroll-common/lib/enrollmentChoiceUtils';
import {
  getCanEnrollThroughS12nSubscriptionFreeTrial,
  getS12nMonthlySkuId,
} from 'bundles/enroll-common/utils/mixAndMatchUtils';

export type straightToCheckoutEnrollParameters = {
  s12nId?: string;
  courseId: string;
  enrollmentAvailableChoices?: EnrollmentChoices;
};

/**
 * Checks whether straight-to-checkout can build a valid checkout for this product.
 * Returns false when there's no course to enter through, no single-course purchase,
 * or (for s12n-subscription courses) no resolvable monthly SKU, in which case we
 * should fall back to the enroll modal.
 */
export const canStraightToCheckoutEnroll = ({
  s12nId,
  courseId,
  enrollmentAvailableChoices,
}: {
  s12nId?: string;
  courseId?: string;
  enrollmentAvailableChoices?: EnrollmentChoices;
}): boolean => {
  if (!courseId) {
    return false;
  }

  if (!s12nId) {
    return Boolean(enrollmentAvailableChoices?.canPurchaseSingleCourse);
  }
  // s12n-subscription course: needs a resolvable monthly SKU for that s12n. `isFromS12nSelection`
  // mirrors useEnrollInS12nSubscription — reads the selected s12n's products, not the arbitrary
  // first one (required for mix-and-match).
  return Boolean(getS12nMonthlySkuId({ enrollmentAvailableChoices, isFromS12nSelection: true, s12nId }));
};

const straightToCheckoutEnroll = async ({
  s12nId,
  courseId,
  enrollmentAvailableChoices,
}: straightToCheckoutEnrollParameters) => {
  if (!canStraightToCheckoutEnroll({ s12nId, courseId, enrollmentAvailableChoices })) {
    return null;
  }

  if (!s12nId && courseId && enrollmentAvailableChoices?.canPurchaseSingleCourse) {
    const handleSubmitPromise = choiceTypeToHandleSubmitPromise[PURCHASE_SINGLE_COURSE];
    const options = { courseId };
    return submitEnrollmentPromise({
      handleSubmitPromise,
      options,
    });
  }

  const enrollmentData = { enrollmentAvailableChoices, isFromS12nSelection: true, s12nId: s12nId ?? '' };

  let skipWelcomeEmail = false;
  if (s12nId && getCanEnrollThroughS12nSubscriptionFreeTrial(enrollmentData)) {
    skipWelcomeEmail = await isS12nEligibleForCourseraPlusUpgrade(s12nId);
  }

  const handleSubmitPromise = choiceTypeToHandleSubmitPromise[ENROLL_THROUGH_S12N_SUBSCRIPTION];
  const options = { productSkuId: getS12nMonthlySkuId(enrollmentData), courseId, skipWelcomeEmail };

  return submitEnrollmentPromise({
    handleSubmitPromise,
    options,
  });
};

export default straightToCheckoutEnroll;
